
// Whitelist http(s), protocol-relative and root-relative URLs.
// Rejects javascript:, data:, vbscript: and other unsafe schemes.
function isSafeRedirectUrl(url) {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('//')) return true;
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return true;
  try {
    const parsed = new URL(trimmed, window.location.href);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (e) {
    return false;
  }
}

// Delay before external redirect so the confirmation message is readable
// and screen readers have time to announce it via aria-live.
const REDIRECT_DELAY_MS = 3000;

// Message copy is hardcoded (never injects the follow-up URL or any user data)
// so the innerHTML assignment stays XSS-safe.
const SUCCESS_MESSAGE_DEFAULT =
  '<div><strong>Success!</strong> Thank you for filling out our form. We have received it and will get back to you soon.</div>';

// Marketo tracking params that would otherwise re-trigger the deep-link
// success path on every subsequent reload of the same page.
const MARKETO_TRACKING_PARAMS = ['aliId', 'mkt_tok'];

// --- Post-submit scroll behavior ---
// Wait a beat before the first scroll so Marketo's own post-submit DOM
// work (form reset, field re-render) has a chance to settle.
const SCROLL_INITIAL_DELAY_MS = 300;
// Y offset for the message top inside the viewport. 200px clears sticky
// site headers, announcement banners, and the Sitecore editor preview
// toolbar without pushing the message so far down that it looks orphaned.
const SCROLL_TOP_OFFSET_PX = 200;
// Bounded retry loop so a mid-flight Marketo mutation can't leave the
// message off-target.
const SCROLL_MAX_ATTEMPTS = 4;
const SCROLL_RETRY_DELAY_MS = 300;
// Slack around SCROLL_TOP_OFFSET_PX before we consider the message
// "drifted" and re-scroll.
const SCROLL_TOLERANCE_PX = 20;
// requestAnimationFrame-based watcher runs alongside the retry loop and
// catches layout shifts that land AFTER the last retry (fonts, images,
// preview chrome). Aborts on any user-driven scroll input.
const LAYOUT_SETTLE_WINDOW_MS = 2000;
// Marketo Forms 2.0 re-focuses the first field after `formElem.reset()`;
// the browser then scrolls that input into view, defeating our scroll to
// the message. A capture-phase `focusin` listener on the form neutralizes
// this for the given window.
const FOCUS_LOCK_WINDOW_MS = 2000;

// Strip Marketo tracking params from the current URL without a navigation.
// Uses history.replaceState so it's a same-origin, in-place update.
function cleanMarketoTrackingParams() {
  if (typeof window === 'undefined' || !window.history || !window.history.replaceState) {
    return;
  }
  try {
    const url = new URL(window.location.href);
    let mutated = false;
    MARKETO_TRACKING_PARAMS.forEach((param) => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        mutated = true;
      }
    });
    if (mutated) {
      const cleaned = url.pathname + (url.search ? url.search : '') + url.hash;
      window.history.replaceState({}, document.title, cleaned);
    }
  } catch (e) {
    // No-op: URL cleanup is a UX nicety, never a hard requirement.
  }
}

// Returns true only when the URL points to a *different* page than the one
// the user is currently on. Marketo's Forms 2.0 API sometimes passes the
// current page URL back as `followUpUrl` for forms that have NO Thank You
// URL configured; navigating to it would cause a full-page reload the user
// didn't ask for, which is the exact regression the review flagged.
//
// Comparison uses origin + pathname only so search params (e.g. Marketo
// tracking) and hash fragments don't force a spurious redirect.
function isExternalRedirect(url) {
  if (!isSafeRedirectUrl(url)) return false;
  try {
    const target = new URL(url.trim(), window.location.href);
    const current = new URL(window.location.href);
    return (
      target.origin !== current.origin ||
      target.pathname !== current.pathname
    );
  } catch (e) {
    return false;
  }
}

export default function initMarketoForms() {
  const munchkinId = '067-YWO-436';
  const forms = document.querySelectorAll('.marketo-form');

  if (forms.length === 0) return;

  forms.forEach(form => {
    const formId = form.querySelector("form").dataset.marketoFormId;

  
    // Falls back to SUCCESS_MESSAGE_DEFAULT when the field is empty.
    const readAuthoredMessage = () => {
      const el = form.querySelector('.js-marketo-success-message');
      const html = el && el.innerHTML ? el.innerHTML.trim() : '';
      return html || null;
    };
    const authoredMessage = readAuthoredMessage();

    const successMessage = document.createElement('div');
    successMessage.classList.add(
      'marketo-form__message',
      'marketo-form__message--success'
    );
    successMessage.setAttribute('role', 'status');
    successMessage.setAttribute('aria-live', 'polite');
    // tabindex=-1 makes the message programmatically focusable without
    // adding it to the natural tab order. Enables post-submit focus
    // move for keyboard users without disrupting normal navigation.
    successMessage.setAttribute('tabindex', '-1');
    successMessage.innerHTML = SUCCESS_MESSAGE_DEFAULT;

    // Attach the message as first child of the `.marketo-form` wrapper
    // and reveal it via the `.is-visible` class (which sets
    // `display: block` in SCSS). Idempotent so it can be safely called
    // again from the scroll retry loop -- some Marketo builds re-render
    // the form container after onSuccess and strip our injected node.
    const attachAndReveal = () => {
      if (!successMessage.isConnected) {
        form.insertAdjacentElement('afterbegin', successMessage);
      }
      successMessage.classList.add('is-visible');
    };

    const showSuccessMessage = () => {
      successMessage.innerHTML = authoredMessage || SUCCESS_MESSAGE_DEFAULT;

      attachAndReveal();

      // Strip Marketo tracking params so a reload doesn't re-fire the
      // deep-link path and re-show the message unexpectedly.
      cleanMarketoTrackingParams();

      // Post-submit scroll strategy:
      //  1. INSTANT scroll to place the message at SCROLL_TOP_OFFSET_PX
      //     below the viewport top. Instant beats smooth because Marketo's
      //     SDK sometimes triggers a competing scroll and a smooth scroll
      //     is interruptible.
      //  2. A bounded retry loop (SCROLL_MAX_ATTEMPTS) covers the case
      //     where Marketo's own DOM mutations shove the target off-band
      //     between our scroll and the next paint.
      //  3. A rAF-based layout-settle watcher runs in parallel and covers
      //     late shifts (font swap, image sizing, preview chrome) that
      //     land after the last retry.
      //  4. Focus moves to the message (with preventScroll so this focus
      //     itself doesn't cause a scroll) for keyboard/a11y users.

      const scrollToMessage = () => {
        const rect = successMessage.getBoundingClientRect();
        const targetY = Math.max(
          0,
          window.pageYOffset + rect.top - SCROLL_TOP_OFFSET_PX
        );
        try {
          window.scrollTo({ top: targetY, behavior: 'auto' });
        } catch (e) {
          // Safari <15.4 rejects the options object -- legacy fallback.
          window.scrollTo(0, targetY);
        }
      };

      const isMessageOnTarget = () => {
        if (!successMessage.isConnected) return false;
        const rect = successMessage.getBoundingClientRect();
        const viewportH =
          window.innerHeight || document.documentElement.clientHeight;
        return (
          rect.height > 0 &&
          rect.top >= SCROLL_TOP_OFFSET_PX - SCROLL_TOLERANCE_PX &&
          rect.top <= SCROLL_TOP_OFFSET_PX + SCROLL_TOLERANCE_PX &&
          rect.bottom <= viewportH
        );
      };

      const attemptScroll = (attempt) => {
        attachAndReveal();
        scrollToMessage();
        setTimeout(() => {
          if (attempt < SCROLL_MAX_ATTEMPTS - 1 && !isMessageOnTarget()) {
            attemptScroll(attempt + 1);
          }
        }, SCROLL_RETRY_DELAY_MS);
      };

      const startLayoutSettleWatcher = () => {
        const startedAt = Date.now();
        let stopped = false;
        const stopWatcher = () => {
          stopped = true;
        };
        const cleanup = () => {
          window.removeEventListener('wheel', stopWatcher);
          window.removeEventListener('touchstart', stopWatcher);
          window.removeEventListener('keydown', stopWatcher);
        };
        window.addEventListener('wheel', stopWatcher, { passive: true });
        window.addEventListener('touchstart', stopWatcher, { passive: true });
        window.addEventListener('keydown', stopWatcher);

        const tick = () => {
          if (
            stopped ||
            Date.now() - startedAt > LAYOUT_SETTLE_WINDOW_MS ||
            !successMessage.isConnected
          ) {
            cleanup();
            return;
          }
          const rect = successMessage.getBoundingClientRect();
          if (Math.abs(rect.top - SCROLL_TOP_OFFSET_PX) > SCROLL_TOLERANCE_PX) {
            scrollToMessage();
          }
          window.requestAnimationFrame(tick);
        };
        window.requestAnimationFrame(tick);
      };

      setTimeout(() => {
        attemptScroll(0);
        startLayoutSettleWatcher();
        try {
          successMessage.focus({ preventScroll: true });
        } catch (e) {
          successMessage.focus();
        }
      }, SCROLL_INITIAL_DELAY_MS);
    };

    // If the browser restores this page from bfcache (e.g. Back button
    // or some reloads on Safari/Firefox), the previously injected message
    // node would still be attached. Reset it so it only appears after a
    // fresh submit or a legitimate ?aliId= deep-link visit.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        successMessage.classList.remove('is-visible');
        if (successMessage.isConnected) {
          successMessage.remove();
        }
      }
    });

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();

        const script = document.createElement('script');
        script.src = `https://${munchkinId}.mktoweb.com/js/forms2/js/forms2.min.js`;
        script.onload = () => {
          MktoForms2.loadForm(
            `https://${munchkinId}.mktoweb.com`,
            munchkinId,
            formId,
            (loadedForm) => {
              if (loadedForm) {
                const formElem = loadedForm.getFormElem()[0];
                formElem.classList.remove('mktoForm');

                // Locate the submit button once so we can reference it in
                // both the pre-submit label snapshot and the post-success
                // reset. Marketo's default markup uses `.mktoButton` inside
                // a `.mktoButtonRow`; we accept either along with a plain
                // `[type="submit"]` fallback so themed forms still match.
                const findSubmitButton = () =>
                  formElem.querySelector(
                    'button.mktoButton, .mktoButtonRow button, button[type="submit"]'
                  );

                // Mark the button as "done" after a successful non-redirect
                // submit. We keep it visibly disabled -- matching the
                // redirect flow's button state -- so users can't resubmit
                // and get a clean completion cue. The label is swapped to
                // "Success" instead of Marketo's stuck "Please wait...".
                //
                // Multi-pass because Marketo's internal end-of-submit code
                // path can re-set the label after our first pass, depending
                // on the SDK build. `submittable(false)` explicitly keeps
                // Marketo's own gate closed so it doesn't try to re-submit.
                const SUCCESS_BUTTON_LABEL = 'Success';
                const markSubmitButtonSucceeded = () => {
                  try {
                    if (typeof loadedForm.submittable === 'function') {
                      loadedForm.submittable(false);
                    }
                  } catch (e) { /* no-op: state gate is best-effort */ }
                  const btn = findSubmitButton();
                  if (!btn) return;
                  btn.textContent = SUCCESS_BUTTON_LABEL;
                  btn.disabled = true;
                  btn.setAttribute('aria-disabled', 'true');
                  btn.removeAttribute('aria-busy');
                };

                // Root-cause fix for the "message scrolls off-viewport" bug.
                //
                // After onSuccess returns, MktoForms2 SDK internally resets
                // the form and focuses the first form input to prep for
                // another entry. When the browser focuses an input, it
                // scrolls that input into view -- overriding our scroll to
                // the message. A capture-phase `focusin` listener scoped
                // to `formElem` catches Marketo's re-focus (and any
                // subsequent one within FOCUS_LOCK_WINDOW_MS) and blurs
                // it, leaving our success-message scroll uncontested.
                // After the window closes we remove the listener so the
                // form is fully interactive again (though the submit button
                // stays disabled because it's in the completed state).
                const lockFormFieldFocus = () => {
                  // Blur anything currently focused inside the form so the
                  // browser doesn't hold focus on an input we just scrolled
                  // past.
                  const active = document.activeElement;
                  if (
                    active &&
                    active !== successMessage &&
                    formElem.contains(active) &&
                    typeof active.blur === 'function'
                  ) {
                    active.blur();
                  }

                  const lockStartedAt = Date.now();
                  const focusInterceptor = (event) => {
                    if (Date.now() - lockStartedAt > FOCUS_LOCK_WINDOW_MS) {
                      formElem.removeEventListener(
                        'focusin',
                        focusInterceptor,
                        true
                      );
                      return;
                    }
                    // Only intercept form-field focus; leave our success
                    // message alone (it's the intended focus target).
                    if (
                      event.target !== successMessage &&
                      event.target &&
                      typeof event.target.blur === 'function'
                    ) {
                      event.target.blur();
                    }
                  };
                  // Capture phase so we run before any bubbling handlers
                  // (including Marketo's own).
                  formElem.addEventListener('focusin', focusInterceptor, true);
                  // Belt-and-suspenders cleanup: remove after the window
                  // even if no focus events fired.
                  setTimeout(() => {
                    formElem.removeEventListener(
                      'focusin',
                      focusInterceptor,
                      true
                    );
                  }, FOCUS_LOCK_WINDOW_MS + 100);
                };

                const mktoFormRows = form.querySelectorAll('.mktoFormRow');
                mktoFormRows.forEach((row) => {
                  row.classList.add('field-container', 'col-6');
                  const fields = row.querySelectorAll('.mktoFormCol');
                  const fieldCount = fields.length;
                  fields.forEach((field) => {
                    field.classList.add('form-field', `col-${6 / fieldCount}`);
                  });
                });

                // Always show the confirmation message so users get visual
                // feedback. If Marketo Admin has a Thank You URL configured,
                // let them read the message briefly, then navigate ourselves.
                loadedForm.onSuccess((values, followUpUrl) => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({ event: 'mkto_form_submit' });

                  // Only redirect when Marketo points us to a *different*
                  // page. If the follow-up URL echoes the current page
                  // (Marketo's default for forms with no Thank You URL),
                  // treat this as an inline confirmation and stay put --
                  // no page reload.
                  const shouldRedirect = isExternalRedirect(followUpUrl);
                  showSuccessMessage();

                  if (shouldRedirect) {
                    // Snapshot now, re-validate at navigation time as
                    // defense-in-depth against any late mutation.
                    const targetUrl = followUpUrl.trim();
                    setTimeout(() => {
                      if (isExternalRedirect(targetUrl)) {
                        window.location.assign(targetUrl);
                      }
                    }, REDIRECT_DELAY_MS);
                  } else {
                    // Non-redirect path: mark the button as succeeded
                    // (label "Success", stays disabled) so it matches the
                    // redirect flow's button state instead of getting
                    // stuck on Marketo's "Please wait...". Multi-pass
                    // beats Marketo's own post-handler re-set that some
                    // SDK builds do.
                    markSubmitButtonSucceeded();
                    setTimeout(markSubmitButtonSucceeded, 0);
                    setTimeout(markSubmitButtonSucceeded, 50);
                    setTimeout(markSubmitButtonSucceeded, 200);

                    // Install the focus lock immediately so Marketo's
                    // post-onSuccess field re-focus can't scroll the page
                    // away from the success message. Must run BEFORE our
                    // 300ms scroll attempt so the focus event, if any,
                    // is intercepted before its scroll takes effect.
                    lockFormFieldFocus();
                  }

                  return false;
                });

                if (window.location.search.includes('aliId=')) {
                  showSuccessMessage();
                }
              }
            }
          );
        };
        document.body.appendChild(script);
      }
    });

    observer.observe(form);
  });
}
